/**
 * Maps a state to the CSS custom property that carries its colour.
 *
 * Takes a plain string rather than the state unions: live socket payloads and
 * the REST snapshot describe the same states, and widening here avoids casting
 * at every call site. Anything unrecognised falls through to the idle colour.
 */
export function statusVar(state: string | null | undefined): string {
  switch (state) {
    case 'normal':
    case 'online':
    case 'ok':
      return 'var(--ok)';
    case 'warning':
    case 'warn':
    case 'degraded':
      return 'var(--warn)';
    case 'major':
      return 'var(--major)';
    case 'critical':
    case 'alarm':
    case 'fault':
    case 'no_signal':
    case 'no_receive':
      return 'var(--crit)';
    case 'offline':
    case 'stale':
    case 'standby':
    case 'unknown':
    case 'maintenance':
    default:
      return 'var(--idle)';
  }
}

const STATE_LABELS: Record<string, string> = {
  normal: 'Normal',
  warning: 'Warning',
  critical: 'Critical',
  no_signal: 'No Signal',
  stale: 'No Data',
  online: 'Online',
  standby: 'Standby',
  degraded: 'Degraded',
  fault: 'Fault',
  no_receive: 'No Receive',
  unknown: 'Unknown',
  alarm: 'Alarm',
  offline: 'Offline',
  maintenance: 'Maintenance',
  info: 'Info',
  major: 'Major',
};

/**
 * Site-level wording, which deliberately differs from the equipment wording.
 *
 * An operator scanning the network map needs to know what to do, not what the
 * state machine calls itself: "Healthy" and "Critical Alarm" are actionable in
 * a way that "Online" and "Alarm" are not. Equipment and checkpoints keep the
 * literal labels below, because "Online" really is the right word for an LNB.
 */
const SITE_STATE_LABELS: Record<string, string> = {
  online: 'Healthy',
  warning: 'Degraded',
  alarm: 'Critical Alarm',
  offline: 'Offline',
  maintenance: 'Maintenance',
};

export type LabelScope = 'site' | 'component';

export function stateLabel(
  state: string | null | undefined,
  scope: LabelScope = 'component',
): string {
  if (!state) return 'Unknown';
  if (scope === 'site') return SITE_STATE_LABELS[state] ?? STATE_LABELS[state] ?? state;
  return STATE_LABELS[state] ?? state;
}

/** The four site states, in the order the legend and the summary tiles use. */
export const SITE_STATES = [
  { state: 'online', label: 'Healthy' },
  { state: 'warning', label: 'Degraded' },
  { state: 'alarm', label: 'Critical Alarm' },
  { state: 'offline', label: 'Offline' },
] as const;

/** Levels are shown to one decimal; N/A when the tap is dark. */
export function dbm(value: number | null | undefined, digits = 0): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return 'N/A';
  return value.toFixed(digits);
}

export function signedDb(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return '—';
  return `${value > 0 ? '+' : ''}${value.toFixed(1)}`;
}

/** 950000000 -> "950.000 MHz" */
export function hz(value: number | null | undefined): string {
  if (value === null || value === undefined) return '—';
  if (value >= 1e9) return `${(value / 1e9).toFixed(3)} GHz`;
  if (value >= 1e6) return `${(value / 1e6).toFixed(3)} MHz`;
  if (value >= 1e3) return `${(value / 1e3).toFixed(1)} kHz`;
  return `${value} Hz`;
}

/** Compact axis label for the spectrum charts. */
export function mhz(value: number): string {
  return (value / 1e6).toFixed(0);
}

const TIME_FMT = new Intl.DateTimeFormat('en-SG', {
  hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false,
  timeZone: 'Asia/Singapore',
});

const DATE_FMT = new Intl.DateTimeFormat('en-SG', {
  day: '2-digit', month: 'short', year: 'numeric', timeZone: 'Asia/Singapore',
});

export function clockTime(d: Date | string | number): string {
  return TIME_FMT.format(new Date(d));
}

export function shortDate(d: Date | string | number): string {
  return DATE_FMT.format(new Date(d));
}

export function dateTime(d: Date | string | number | null | undefined): string {
  if (!d) return '—';
  return `${DATE_FMT.format(new Date(d))} ${TIME_FMT.format(new Date(d))} SGT`;
}

/** "3 min ago", "just now" - for last-seen columns. */
export function relative(iso: string | null | undefined): string {
  if (!iso) return 'never';
  const delta = Date.now() - new Date(iso).getTime();
  if (delta < 5_000) return 'just now';
  if (delta < 60_000) return `${Math.floor(delta / 1000)} s ago`;
  if (delta < 3_600_000) return `${Math.floor(delta / 60_000)} min ago`;
  if (delta < 86_400_000) return `${Math.floor(delta / 3_600_000)} h ago`;
  return `${Math.floor(delta / 86_400_000)} d ago`;
}

/** Icon glyph per equipment kind, used in the chain diagram and health tiles. */
export function equipmentGlyph(kind: string): string {
  switch (kind) {
    case 'antenna': return '◉';
    case 'lnb': return '◈';
    case 'bias_t': return '⊺';
    case 'acu': return '▤';
    case 'down_converter': return '⇊';
    case 'ifdu': return '▥';
    case 'system': return '▣';
    default: return '▢';
  }
}
